import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { format } from 'utils/time/format';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import groupBy from 'utils/arrays/groupBy';
import parseISO from 'utils/time/parseISO';
import useNotification from 'containers/App/Notifications/useNotification';
import formatISO from 'utils/time/formatISO';
import serializeDateToServer from 'utils/time/serializeDateToServer';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { deserializeEvent } from 'containers/CahierJournal/queries/serializer';
import { addToCJPastAtom } from 'containers/CahierJournal/queries/historyAtoms';
import { useAtom } from 'jotai';
import {
  ClassroomMapping,
  NO_COPY,
} from 'components/EntitiesSelectorModals/ClassroomsMappingModal/ClassroomsMapping';
import { CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY } from 'containers/CahierJournal/queries/useGetDaysWithEvents';
import { useTranslation } from 'react-i18next';
import { CJEventApi } from 'types/event';

interface Props {
  from: Date;
  to: Date;
  srcSchoolId: string;
  srcSchoolyearId: string;
  destSchoolId: string;
  destSchoolyearId: string;
  eventIds?: string[];
  classroomsMapping?: ClassroomMapping[];
}

export function useCopyCJSeances(
  disableUndoRedo?: boolean,
): [(tt: Props) => Promise<{ data: CJEventApi[] }>, boolean] {
  const { notifyError } = useNotification();
  const [, addToPast] = useAtom(addToCJPastAtom);
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['cahierJournal']);
  const user = useUser();

  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: ({
      from,
      to,
      eventIds,
      classroomsMapping,
      srcSchoolId,
      srcSchoolyearId,
    }: Props) => {
      const requestURL = `/schoolyears/${srcSchoolyearId}/schools/${srcSchoolId}/cahier-journal/copy`;
      return request(requestURL, {
        method: 'POST',
        body: {
          from: formatISO(serializeDateToServer(from, user.location)),
          to: formatISO(serializeDateToServer(to, user.location)),
          eventIds,
          classroomsMapping: classroomsMapping?.reduce((acc, curr) => {
            if (!curr.to || curr.to === NO_COPY) {
              return acc;
            }
            acc[curr.from] = curr.to;
            return acc;
          }, {}),
        },
      });
    },
    onSuccess: ({ data: cjEvents }, { destSchoolyearId, destSchoolId }) => {
      if (!disableUndoRedo) {
        addToPast({
          payload: cjEvents.map(ev => deserializeEvent(ev, user.location)),
          action: 'CREATE',
        });
      }
      const dates = Object.keys(
        groupBy(cjEvents, e => format(parseISO(e.start), 'fr-FR')),
      );
      dates.forEach(d => {
        queryClient.invalidateQueries({
          queryKey: [destSchoolyearId, destSchoolId, EVENTS_KEY, d],
        });
      });

      queryClient.invalidateQueries({
        queryKey: [
          schoolyear.id,
          school.id,
          CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY,
        ],
      });
    },
    onError: () => {
      notifyError({
        title: t('cahierJournal:messages.copyActivitiesError'),
      });
    },
  });
  return [mutateAsync, isLoading];
}
